import { EMPTY_STRING } from '../../../../../constants/common'
import { isValueFunctionType } from '../../../../../utils/common'

function createUrlSource(initialImageUrl) {
  return initialImageUrl ? { name: 'image', url: initialImageUrl } : null
}

function applyStageImageStyle(element) {
  element.style.width = '100%'
  element.style.height = '100%'
  element.style.display = 'block'
  element.style.pointerEvents = 'none'
}

// Reads the whole bitmap once and tracks the extent of any opaque pixel.
// Returns null when the image is fully transparent.
function getAlphaBounds(context, width, height) {
  const { data } = context.getImageData(0, 0, width, height)

  let top = null
  let bottom = null
  let left = width - 1
  let right = 0

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const isOpaque = data[(y * width + x) * 4 + 3] > 0

      if (isOpaque) {
        if (top === null) {
          top = y
        }

        bottom = y
        left = Math.min(left, x)
        right = Math.max(right, x)
      }
    }
  }

  return top === null ? null : { bottom, left, right, top }
}

export function createInitialSource({ initialFile, initialImageUrl }) {
  if (initialFile) {
    return { name: initialFile.name, url: URL.createObjectURL(initialFile) }
  }

  return createUrlSource(initialImageUrl)
}

// Object URLs (created for picked Files) need revoking; existing image URLs don't.
export function isObjectUrl(url) {
  return Boolean(url) && url.startsWith('blob:')
}

// The hook owns and styles the <img>; the stage mounts it as-is.
export function createStageImage() {
  const image = new Image()

  image.crossOrigin = 'anonymous'
  image.alt = EMPTY_STRING
  applyStageImageStyle(image)

  return image
}

// Crops fully transparent margins off the decoded image (e.g. a thumbnail
// padded to a square), so the crop frame can never cover empty pixels. Returns
// the original image when there is nothing to trim or the pixel data is
// unreadable (tainted canvas).
export function getTrimmedStageImage(image) {
  const { naturalHeight, naturalWidth } = image

  const canvas = document.createElement('canvas')

  canvas.width = naturalWidth
  canvas.height = naturalHeight

  let bounds = null

  try {
    const context = canvas.getContext('2d')

    context.drawImage(image, 0, 0)

    bounds = getAlphaBounds(context, naturalWidth, naturalHeight)
  } catch {
    return image
  }

  if (!bounds) {
    return image
  }

  const width = bounds.right - bounds.left + 1
  const height = bounds.bottom - bounds.top + 1

  if (width === naturalWidth && height === naturalHeight) {
    return image
  }

  const trimmedCanvas = document.createElement('canvas')

  trimmedCanvas.width = width
  trimmedCanvas.height = height

  const trimmedContext = trimmedCanvas.getContext('2d')

  if (!trimmedContext) {
    return image
  }

  trimmedContext.drawImage(
    canvas,
    bounds.left,
    bounds.top,
    width,
    height,
    0,
    0,
    width,
    height
  )

  applyStageImageStyle(trimmedCanvas)

  return trimmedCanvas
}

// The stage element is either the decoded <img> or a trimmed canvas.
export function getStageImageSize(element) {
  return {
    height: element.naturalHeight ?? element.height,
    width: element.naturalWidth ?? element.width,
  }
}

export function decodeImage(image) {
  return isValueFunctionType(image.decode) ? image.decode() : Promise.resolve()
}

// A decoded image can still report zero intrinsic size (e.g. a sizeless SVG),
// which would break the fit math — treat it as a load failure.
export function hasIntrinsicSize(image) {
  return image.naturalWidth > 0 && image.naturalHeight > 0
}
